import "./index.css";
import { clsx } from "clsx";
import type { ElementType, MouseEvent } from "react";
import { useCallback } from "react";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { Spinner } from "../Spinner";
import type { ISpinnerSize } from "../Spinner/types";
import { Text } from "../Text";
import type { ITextSize } from "../Text/types";
import { ButtonIcon } from "./ButtonIcon";
import type {
  ButtonBaseProps,
  IButtonColor,
  IButtonSize,
  IButtonSpinnerPosition,
  IButtonVariant,
} from "./types";

const textSize: Record<IButtonSize, ITextSize> = {
  xs: "sm",
  sm: "sm",
  md: "md",
  lg: "md",
};

export const BaseButton = <C extends ElementType = "button">(
  props: ButtonBaseProps & { as?: C },
) => {
  const {
    // Custom props (not passed to element)
    as,
    loading,
    spinnerPosition: spinnerPositionProp,
    variant,
    color,
    content,
    size,
    startIcon,
    endIcon,
    allowClickOnLoading,
    isIcon: isIconProp,
    spinnerProps,
    // Props we need to reference
    children,
    className,
    type = "button",
    onClick,
    disabled,
    // Rest goes to element
    ...restProps
  } = props;

  const Component = as || "button";

  const isMobile = useMediaQuery("(max-width: 639px)");

  const buttonVariant: IButtonVariant = variant || "solid";
  const buttonColor: IButtonColor = color || "brand";
  const buttonSize: IButtonSize = size || (isMobile ? "lg" : "md");

  const isIcon = !!isIconProp;
  const spinnerDefaultSize: ISpinnerSize = buttonSize === "xs" ? "xs" : "sm";
  const spinnerPosition: IButtonSpinnerPosition = isIcon
    ? "center"
    : spinnerPositionProp || "center";

  const showStartIcon: boolean =
    !isIcon &&
    startIcon !== undefined &&
    !(spinnerPosition === "start" && loading === true);
  const showEndIcon: boolean =
    !isIcon &&
    endIcon !== undefined &&
    !(spinnerPosition === "end" && loading === true);
  const showSpinner: boolean = loading === true && !disabled;

  const rootClass: string = clsx(
    "nuco-button",
    `nuco-button--${buttonVariant}`,
    `nuco-color-${buttonColor}`,
    `nuco-button--${buttonSize}`,
    `nuco-button--spinner-${spinnerPosition}`,
    isIcon && "nuco-button--icon",
    loading && "nuco-button--loading",
    disabled && "nuco-button--disabled",
    className,
  );

  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      if (!allowClickOnLoading && loading) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (onClick) {
        onClick(e);
      }
    },
    [onClick, allowClickOnLoading, loading],
  );

  const componentProps =
    Component === "button" ? { type, disabled, ...restProps } : restProps;

  const Tag = Component as ElementType;

  return (
    <Tag
      {...componentProps}
      className={rootClass}
      onClick={handleClick}
      aria-busy={loading}
      aria-disabled={disabled}
    >
      {showSpinner && spinnerPosition === "start" && (
        <Spinner
          className="nuco-button__spinner"
          size={spinnerDefaultSize}
          {...spinnerProps}
        />
      )}
      {showStartIcon && (
        <div className="nuco-button__icon">
          <ButtonIcon icon={startIcon} size={buttonSize} />
        </div>
      )}
      <Text
        size={textSize[buttonSize]}
        weight="medium"
        className="nuco-button__text"
      >
        {children || content}
      </Text>
      {showEndIcon && (
        <div className="nuco-button__icon">
          <ButtonIcon icon={endIcon} size={buttonSize} />
        </div>
      )}
      {showSpinner && spinnerPosition === "center" && (
        <Spinner
          className="nuco-button__spinner"
          size={spinnerDefaultSize}
          {...spinnerProps}
        />
      )}
      {showSpinner && spinnerPosition === "end" && (
        <Spinner
          className="nuco-button__spinner"
          size={spinnerDefaultSize}
          {...spinnerProps}
        />
      )}
    </Tag>
  );
};
